import { Box, Tooltip, tooltipClasses, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import IconCross from 'assets/img/icons/cross.svg?react';
import {
  IconCalendar,
  IconCertificate,
  IconClock,
  IconCloseCircle,
  IconLocation,
  IconPause,
  IconProfileUsers,
} from 'assets/img';
import { LwIconButton, ShiftDescriptionTooltip } from 'redesign';
import { PlannerShiftResponse } from 'partner/shared/services/partner-planner-service.types';
import { CertificatesDetail } from 'shared/components/planner/common/certificates-detail';
import { getShiftProperties } from './utils/get-shift-properties';

type ShiftDetailHeadProps = {
  data: PlannerShiftResponse;
  close: () => void;
};

export const PartnerPlannerModalHead = ({ data, close }: ShiftDetailHeadProps) => {
  const shiftProperties = getShiftProperties(data);

  return (
    <Box>
      <Box display="flex" flexDirection="row">
        <CompanyInfoBox>
          <Typography noWrap color="secondary" variant="body1" style={{ fontWeight: 'bold' }}>
            {shiftProperties.businessUnitInfo}
          </Typography>
          <FullWidthTooltip title={shiftProperties.jobName} placement="bottom-start">
            <StyledJobName noWrap variant="h5">
              {shiftProperties.jobName}
            </StyledJobName>
          </FullWidthTooltip>
        </CompanyInfoBox>
        <Box display="flex" flexDirection="row" ml="auto" alignItems="center" gap={8}>
          <LwIconButton
            iconColorMode="stroke"
            color="secondary"
            onClick={close}
            data-testid="partner-planner-modal-close"
          >
            <IconCross width={24} height={24} />
          </LwIconButton>
        </Box>
      </Box>
      <Box gap={3} display="flex" flexDirection="row" marginTop={5}>
        <Box display="flex" flexDirection="column" flex={2} minWidth={0}>
          <Box display="flex" flexDirection="row" marginBottom={3}>
            <StyledUsersIcon />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.seats}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" marginBottom={3}>
            <StyledCalendarIcon />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.startDate}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" flex={2} minWidth={0}>
          <Box display="flex" flexDirection="row" marginBottom={3}>
            <StyledClockIcon />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.duration}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" marginBottom={3}>
            <StyledPauseIcon />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.break}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" marginBottom={3} flex={1}>
            <StyledCertificateIcon />
            <CertificatesDetail certificates={data.certificates} />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" flex={1.5}>
          <Box display="flex" flexDirection="row" marginBottom={3} flex={1}>
            <StyledCloseIcon />
            <Tooltip
              title={
                shiftProperties.cancelationPeriod === 'Verstreken'
                  ? 'Annuleringstermijn verstreken'
                  : `Annuleringstermijn eindigt over: ${shiftProperties.cancelationPeriod}`
              }
            >
              <Typography noWrap variant="subtitle2">
                {shiftProperties.cancelationPeriod}
              </Typography>
            </Tooltip>
          </Box>
          <Box display="flex" flexDirection="row" marginBottom={3} flex={1}>
            <StyledPauseIcon />
            <Typography noWrap variant="subtitle2">
              {shiftProperties.cooldownPeriod}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" marginBottom={3} flex={1}>
            <StyledLocationIcon />
            <Tooltip title={data.location.address}>
              <Typography noWrap variant="subtitle2">
                {shiftProperties.location}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" flex={1.5}>
          <CenterTextBox>
            <ShiftDescriptionTooltip
              descriptionShort={shiftProperties.descriptionShort}
              descriptionLong={shiftProperties.descriptionLong}
              descriptionLawRequirements={shiftProperties.descriptionLawRequirements}
            />
          </CenterTextBox>
        </Box>
      </Box>
    </Box>
  );
};

const StyledCloseIcon = styled(IconCloseCircle)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledPauseIcon = styled(IconPause)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledClockIcon = styled(IconClock)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledCalendarIcon = styled(IconCalendar)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledUsersIcon = styled(IconProfileUsers)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledCertificateIcon = styled(IconCertificate)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledLocationIcon = styled(IconLocation)(({ theme }) => ({
  opacity: 0.6,
  marginRight: theme.spacing(3),
  flexShrink: 0,
  width: 24,
  height: 24,
}));

const StyledJobName = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const CompanyInfoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const CenterTextBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(3),
}));

const FullWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
}));
